import { useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Icon } from '../components/Icon';
import { api } from '../data/api';
import { LeadDrawer } from '../components/LeadDrawer';
import { NewClientModal } from '../components/NewClientModal';
import type { Lead, LeadStatus, MessageTemplate } from '../data/types';

export const LEAD_STATUS: Record<LeadStatus, { label: string; cls: string }> = {
  new: { label: 'Nuovo', cls: 'ls-new' },
  contacted: { label: 'Contattato', cls: 'ls-contacted' },
  replied: { label: 'Ha risposto', cls: 'ls-replied' },
  meeting: { label: 'Incontro', cls: 'ls-meeting' },
  won: { label: 'Vinto', cls: 'ls-won' },
  lost: { label: 'Perso', cls: 'ls-lost' },
};

const FILTERS: { id: 'all' | LeadStatus; label: string }[] = [
  { id: 'all', label: 'Tutti' },
  { id: 'new', label: 'Nuovi' },
  { id: 'contacted', label: 'Contattati' },
  { id: 'replied', label: 'Risposti' },
  { id: 'meeting', label: 'Incontri' },
  { id: 'won', label: 'Vinti' },
  { id: 'lost', label: 'Persi' },
];

/** "2026-06-09T…" → "9 giu" relative-ish short. */
function shortAgo(iso: string | null): string {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (days <= 0) return 'oggi';
  if (days === 1) return 'ieri';
  if (days < 30) return `${days} g fa`;
  return `${Math.floor(days / 30)} mesi fa`;
}

export function LeadsView() {
  const [leads, setLeads] = useState<Lead[] | null>(null);
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [filter, setFilter] = useState<'all' | LeadStatus>('all');
  const [selected, setSelected] = useState<Lead | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [finderOpen, setFinderOpen] = useState(false);
  const [convert, setConvert] = useState<Lead | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Selezione multipla per pulizia lista (checkbox, shift = intervallo, elimina in blocco).
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Ancora dello Shift-select: un ID (non un indice), così resta valida anche se la
  // lista cambia (import, modifica stato, ricarica) e non va mai fuori-range.
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const load = async () => {
    try {
      const [ls, tpl] = await Promise.all([api.leads(), api.leadTemplates()]);
      setLeads(ls);
      setTemplates(tpl);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const list = useMemo(() => (leads ?? []).filter((l) => filter === 'all' || l.status === filter), [leads, filter]);
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const l of leads ?? []) c[l.status] = (c[l.status] ?? 0) + 1;
    return c;
  }, [leads]);

  // Cambiare filtro azzera la selezione: si seleziona sempre dentro la lista visibile.
  useEffect(() => {
    setSelectedIds(new Set());
    setAnchorId(null);
    setConfirming(false);
  }, [filter]);

  const allChecked = list.length > 0 && list.every((l) => selectedIds.has(l.id));
  const someChecked = list.some((l) => selectedIds.has(l.id));
  const selCount = list.reduce((n, l) => n + (selectedIds.has(l.id) ? 1 : 0), 0);

  const toggleAll = (checked: boolean) => {
    setSelectedIds(checked ? new Set(list.map((l) => l.id)) : new Set());
    setAnchorId(null);
    setConfirming(false);
  };
  // Click su una checkbox; con Shift seleziona/deseleziona l'intervallo dall'ultima
  // cliccata. L'ancora è un ID risolto sulla lista CORRENTE ad ogni click: se la lista
  // è cambiata (o l'ancora non c'è più) si comporta come un click singolo, mai crash.
  const toggleAt = (i: number, shift: boolean) => {
    const id = list[i].id;
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const turnOn = !prev.has(id);
      const anchorIdx = anchorId ? list.findIndex((l) => l.id === anchorId) : -1;
      if (shift && anchorIdx !== -1) {
        const [a, b] = anchorIdx <= i ? [anchorIdx, i] : [i, anchorIdx];
        for (let k = a; k <= b; k++) {
          const row = list[k];
          if (!row) continue;
          if (turnOn) next.add(row.id);
          else next.delete(row.id);
        }
      } else if (turnOn) next.add(id);
      else next.delete(id);
      return next;
    });
    setAnchorId(id);
    setConfirming(false);
  };
  const clearSel = () => {
    setSelectedIds(new Set());
    setAnchorId(null);
    setConfirming(false);
  };
  const bulkDelete = async () => {
    const ids = list.filter((l) => selectedIds.has(l.id)).map((l) => l.id);
    if (!ids.length || bulkBusy) return;
    setBulkBusy(true);
    setErr(null);
    try {
      await api.bulkDeleteLeads(ids);
      const gone = new Set(ids);
      setLeads((prev) => prev?.filter((l) => !gone.has(l.id)) ?? null);
      clearSel();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const onLeadChange = (u: Lead) => {
    setLeads((prev) => prev?.map((l) => (l.id === u.id ? u : l)) ?? null);
    setSelected((s) => (s && s.id === u.id ? u : s));
  };
  const onLeadDelete = (id: string) => {
    setLeads((prev) => prev?.filter((l) => l.id !== id) ?? null);
    setSelected(null);
  };

  return (
    <div className="view">
      <div className="view-pad">
        <div className="rs-toolbar" style={{ justifyContent: 'space-between' }}>
          <div className="rs-seg">
            {FILTERS.map((f) => (
              <button key={f.id} className={filter === f.id ? 'on' : ''} onClick={() => setFilter(f.id)}>
                {f.label}
                {f.id !== 'all' && counts[f.id] ? <span className="ls-count">{counts[f.id]}</span> : null}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-ghost" onClick={() => setFinderOpen(true)}>
              <Icon name="search" size={16} />
              <span className="btn-label">Trova lead</span>
            </button>
            <button type="button" className="btn btn-primary" onClick={() => setNewOpen(true)}>
              <Icon name="plus" size={16} />
              <span className="btn-label">Nuovo lead</span>
            </button>
          </div>
        </div>

        {err && <div className="rs-error">Errore: {err}</div>}
        {leads === null ? (
          <div className="rs-muted" style={{ padding: '24px 4px' }}>Carico i lead…</div>
        ) : (
          <>
            {selCount > 0 && (
              <div className="rs-bulkbar">
                <span>
                  <b>{selCount}</b> {selCount === 1 ? 'lead selezionato' : 'lead selezionati'}
                </span>
                <div className="grow" />
                {confirming ? (
                  <>
                    <span className="rs-dim rs-hide-sm">Eliminare {selCount}? Non si annulla.</span>
                    <button type="button" className="btn btn-ghost" onClick={() => setConfirming(false)} disabled={bulkBusy}>
                      Annulla
                    </button>
                    <button type="button" className="btn btn-danger" onClick={bulkDelete} disabled={bulkBusy}>
                      {bulkBusy ? 'Elimino…' : `Sì, elimina ${selCount}`}
                    </button>
                  </>
                ) : (
                  <>
                    <button type="button" className="btn btn-ghost" onClick={clearSel}>
                      Deseleziona
                    </button>
                    <button type="button" className="btn btn-danger" onClick={() => setConfirming(true)}>
                      <Icon name="alert" size={15} />
                      <span className="btn-label">Elimina</span>
                    </button>
                  </>
                )}
              </div>
            )}
            <div className="rs-table leads">
              <div className="rs-thead">
                <span className="rs-check">
                  <input
                    type="checkbox"
                    aria-label="Seleziona tutti"
                    checked={allChecked}
                    ref={(el) => {
                      if (el) el.indeterminate = !allChecked && someChecked;
                    }}
                    onChange={(e) => toggleAll(e.target.checked)}
                  />
                </span>
                <span>Lead</span>
                <span className="rs-hide-sm">Sito</span>
                <span className="rs-hide-sm">Contatti</span>
                <span>Stato</span>
                <span className="rs-hide-sm">Contattato</span>
                <span className="rs-hide-sm">Follow-up</span>
              </div>
              {list.map((l, i) => (
                <div
                  className={'rs-row' + (selectedIds.has(l.id) ? ' sel' : '')}
                  key={l.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelected(l)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setSelected(l);
                  }}
                >
                  <span className="rs-check" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Seleziona ${l.name}`}
                      checked={selectedIds.has(l.id)}
                      onChange={() => {}}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleAt(i, e.shiftKey);
                      }}
                    />
                  </span>
                  <span className="rs-cli">
                    <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                      <span className="rs-cli-name">{l.name}</span>
                      <span className="rs-cli-meta">{[l.category, l.locality].filter(Boolean).join(' · ') || '—'}</span>
                    </span>
                  </span>
                  <span className="rs-cell rs-hide-sm">
                    {l.website ? (
                      <span className="ls-site has">ha un sito</span>
                    ) : (
                      <span className="ls-site none">senza sito</span>
                    )}
                  </span>
                  <span className="rs-cell rs-hide-sm ls-contacts">
                    {l.phone && <Icon name="message" size={14} />}
                    {l.email && <Icon name="globe" size={14} />}
                    {!l.phone && !l.email && <span className="rs-dim">—</span>}
                  </span>
                  <span className="rs-cell">
                    <span className={'rs-chip ls ' + LEAD_STATUS[l.status].cls}>{LEAD_STATUS[l.status].label}</span>
                  </span>
                  <span className="rs-cell rs-hide-sm rs-dim">{shortAgo(l.lastContactedAt)}</span>
                  <span className="rs-cell rs-hide-sm rs-dim">{l.nextFollowUp ?? '—'}</span>
                </div>
              ))}
              {list.length === 0 && (
                <div className="rs-muted" style={{ padding: '24px 18px', fontSize: 13 }}>
                  Nessun lead in questo stato.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {selected && (
        <LeadDrawer
          lead={selected}
          templates={templates}
          onClose={() => setSelected(null)}
          onChange={onLeadChange}
          onDelete={onLeadDelete}
          onConvert={(l) => {
            setSelected(null);
            setConvert(l);
          }}
        />
      )}

      {newOpen && (
        <NewLeadModal
          onClose={() => setNewOpen(false)}
          onCreated={(l) => {
            setLeads((prev) => [l, ...(prev ?? [])]);
            setNewOpen(false);
          }}
        />
      )}

      {finderOpen && (
        <FinderModal
          onClose={() => setFinderOpen(false)}
          onDone={() => {
            setFinderOpen(false);
            void load();
          }}
        />
      )}

      {convert && (
        <NewClientModal
          onClose={() => setConvert(null)}
          prefill={{ name: convert.name, category: convert.category ?? undefined, ownerEmail: convert.email ?? undefined }}
          leadId={convert.id}
          onConverted={() => {
            setConvert(null);
            void load();
          }}
        />
      )}
    </div>
  );
}

// ── inline "Nuovo lead" modal ────────────────────────────────
function NewLeadModal({ onClose, onCreated }: { onClose: () => void; onCreated: (l: Lead) => void }) {
  const [f, setF] = useState({ name: '', category: '', locality: '', phone: '', email: '', website: '', notes: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setF((prev) => ({ ...prev, [k]: e.target.value }));

  const submit = async () => {
    if (!f.name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const lead = await api.createLead({
        name: f.name.trim(),
        category: f.category.trim() || undefined,
        locality: f.locality.trim() || undefined,
        phone: f.phone.trim() || undefined,
        email: f.email.trim() || undefined,
        website: f.website.trim() || undefined,
        notes: f.notes.trim() || undefined,
      });
      onCreated(lead);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg === 'duplicate' ? 'Questa attività è già tra i lead — niente doppioni.' : `Errore: ${msg}`);
      setBusy(false);
    }
  };

  return (
    <div className="mscrim open" onMouseDown={onClose}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Nuovo lead" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Nuovo lead</h3>
          <button type="button" className="dr-close" onClick={onClose} aria-label="Chiudi">
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className="modal-body">
          <div className="field">
            <label htmlFor="nl-name">Nome attività</label>
            <input id="nl-name" className="input" value={f.name} placeholder="Trattoria del Corso" autoFocus onChange={set('name')} />
          </div>
          <div className="field-row">
            <div className="field">
              <label>Tipo</label>
              <input className="input" value={f.category} placeholder="Trattoria" onChange={set('category')} />
            </div>
            <div className="field">
              <label>Città / zona</label>
              <input className="input" value={f.locality} placeholder="Napoli" onChange={set('locality')} />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Telefono / WhatsApp</label>
              <input className="input" value={f.phone} placeholder="+39 081 000 0000" onChange={set('phone')} />
            </div>
            <div className="field">
              <label>Email</label>
              <input className="input" type="email" value={f.email} placeholder="info@…" onChange={set('email')} />
            </div>
          </div>
          <div className="field">
            <label>Sito attuale (lascia vuoto se non ce l’ha)</label>
            <input className="input" value={f.website} placeholder="—" onChange={set('website')} />
          </div>
          <div className="field">
            <label>Note</label>
            <textarea className="input" rows={2} value={f.notes} onChange={set('notes')} />
          </div>
          {error && <div className="rs-error">{error}</div>}
        </div>
        <div className="modal-foot">
          <div className="grow" />
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Annulla</button>
          <button type="button" className="btn btn-primary" onClick={submit} disabled={!f.name.trim() || busy}>
            {busy ? 'Salvo…' : 'Aggiungi lead'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── "Trova lead" — paste importer (self-serve) ───────────────
// Flexible: one lead per line, fields separated by comma/semicolon. The
// importer classifies email/phone/website automatically. (A server-side
// search integration can later feed this same /import endpoint.)
function parseLeads(text: string): Partial<Lead>[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((line) => {
      const parts = line.split(/[;,\t]/).map((s) => s.trim()).filter(Boolean);
      const name = parts.shift() ?? '';
      const lead: Partial<Lead> = { name, source: 'finder' };
      for (const p of parts) {
        if (p.includes('@')) lead.email = p;
        else if (/^https?:\/\//i.test(p) || /\b[a-z0-9-]+\.(it|com|net|eu|org)\b/i.test(p)) lead.website = p;
        else if (/^[\d +().\-/]+$/.test(p) && (p.match(/\d/g) ?? []).length >= 6) lead.phone = p;
        else if (!lead.locality) lead.locality = p;
        else if (!lead.category) lead.category = p;
      }
      return lead;
    })
    .filter((l) => l.name);
}

// Categorie selezionabili nella ricerca → tipi OSM (amenity). Multi-selezione.
const FINDER_CATS: { id: string; label: string; amenities: string[] }[] = [
  { id: 'restaurant', label: 'Ristoranti', amenities: ['restaurant'] },
  { id: 'fast_food', label: 'Fast food', amenities: ['fast_food'] },
  { id: 'bar', label: 'Bar', amenities: ['bar'] },
  { id: 'pub', label: 'Pub', amenities: ['pub', 'biergarten'] },
  { id: 'cafe', label: 'Caffè', amenities: ['cafe'] },
  { id: 'ice_cream', label: 'Gelaterie', amenities: ['ice_cream'] },
];

function FinderModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);
  const parsed = useMemo(() => parseLeads(text), [text]);

  // Ricerca automatica per città (OpenStreetMap) → riempie la textarea sotto,
  // così il flusso di import (con dedup) resta identico.
  const [city, setCity] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchMsg, setSearchMsg] = useState<string | null>(null);
  // Categorie selezionate (default: Ristoranti). Multi-selezione.
  const [cats, setCats] = useState<string[]>(['restaurant']);
  const toggleCat = (id: string) =>
    setCats((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));
  const search = async () => {
    const q = city.trim();
    if (q.length < 2 || searching) return;
    setSearching(true);
    setError(null);
    setSearchMsg(null);
    try {
      const amenities = FINDER_CATS.filter((ct) => cats.includes(ct.id)).flatMap((ct) => ct.amenities);
      const r = await api.findLeads(q, amenities);
      const lines = r.results.map((x) => [x.name, x.locality, x.category, x.phone, x.email].filter(Boolean).join(', '));
      if (lines.length) setText((prev) => (prev.trim() ? prev.trim() + '\n' : '') + lines.join('\n'));
      setSearchMsg(
        r.results.length
          ? `Trovate ${r.results.length} attività senza sito${r.dupes ? ` · ${r.dupes} già in lista, saltate` : ''}. Rivedi qui sotto e importa.`
          : `Nessuna nuova attività senza sito${r.dupes ? ` (${r.dupes} già in lista)` : ''}. Prova un'altra città o incolla a mano.`,
      );
    } catch (e) {
      const m = e instanceof Error ? e.message : String(e);
      setError(
        m.includes('404')
          ? `Comune "${q}" non trovato — prova col nome esatto del comune.`
          : m.includes('502')
            ? 'OpenStreetMap è occupato in questo momento — riprova tra poco.'
            : `Ricerca non riuscita: ${m}`,
      );
    } finally {
      setSearching(false);
    }
  };

  const submit = async () => {
    if (!parsed.length || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.importLeads(parsed);
      setResult({ imported: r.imported, skipped: r.skipped });
      setBusy(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <div className="mscrim open" onMouseDown={() => (result ? onDone() : onClose())}>
      <div className="modal" role="dialog" aria-modal="true" aria-label="Trova lead" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Trova lead</h3>
          <button type="button" className="dr-close" onClick={() => (result ? onDone() : onClose())} aria-label="Chiudi">
            <Icon name="x" size={18} />
          </button>
        </div>
        {result ? (
          <>
            <div className="modal-body">
              <div className="rs-note">
                <b>{result.imported}</b> {result.imported === 1 ? 'lead aggiunto' : 'lead aggiunti'} alla board.
                {result.skipped > 0 && (
                  <>
                    {' '}
                    <b>{result.skipped}</b> {result.skipped === 1 ? 'saltato' : 'saltati'} perché già in lista (così non
                    contattate due volte la stessa attività).
                  </>
                )}
              </div>
            </div>
            <div className="modal-foot">
              <div className="grow" />
              <button type="button" className="btn btn-primary" onClick={onDone}>Fine</button>
            </div>
          </>
        ) : (
          <>
            <div className="modal-body">
              <div className="rs-note">
                Scrivi una <b>città</b>, scegli i <b>tipi</b> e premi Cerca: trovo le attività <b>senza sito</b> (via
                OpenStreetMap, gratis). Oppure incolla nomi a mano qui sotto — uno per riga.
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                {FINDER_CATS.map((ct) => {
                  const on = cats.includes(ct.id);
                  return (
                    <button
                      key={ct.id}
                      type="button"
                      onClick={() => toggleCat(ct.id)}
                      aria-pressed={on}
                      className="btn btn-sm"
                      style={{
                        borderRadius: 999,
                        border: '1px solid var(--rule, rgba(0,0,0,0.16))',
                        background: on ? 'var(--accent, #2F3D2E)' : 'transparent',
                        color: on ? '#fff' : 'inherit',
                        fontWeight: on ? 600 : 500,
                        opacity: on ? 1 : 0.72,
                      }}
                    >
                      {on ? '✓ ' : ''}
                      {ct.label}
                    </button>
                  );
                })}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <input
                  className="input"
                  placeholder="Cerca per città — es. Monopoli"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && cats.length) { e.preventDefault(); void search(); } }}
                  disabled={searching}
                  style={{ flex: 1 }}
                />
                <button type="button" className="btn btn-primary" onClick={() => void search()} disabled={searching || city.trim().length < 2 || cats.length === 0}>
                  {searching ? 'Cerco…' : 'Cerca'}
                </button>
              </div>
              {searchMsg && <div className="rs-muted" style={{ fontSize: 12.5, marginTop: 6 }}>{searchMsg}</div>}
              <textarea
                className="input"
                rows={8}
                value={text}
                placeholder={'Trattoria del Corso, Roma, +39 06 555 1234\nPizzeria Bella, Napoli, info@bella.it'}
                onChange={(e) => setText(e.target.value)}
                style={{ marginTop: 10 }}
              />
              <div className="rs-muted" style={{ fontSize: 12.5, marginTop: 6 }}>
                {parsed.length} lead rilevati. I duplicati (già in lista) vengono saltati in automatico. Oppure{' '}
                <b>chiedi a Risma</b> di cercare i ristoranti senza sito di una città.
              </div>
              {error && <div className="rs-error">{error}</div>}
            </div>
            <div className="modal-foot">
              <div className="grow" />
              <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>Annulla</button>
              <button type="button" className="btn btn-primary" onClick={submit} disabled={!parsed.length || busy}>
                {busy ? 'Importo…' : `Importa ${parsed.length} lead`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
